import api from "@/services/apiClient";
import axios from "axios";
import type {
  ApiCustomerEnvironment,
  CustomerEnvironment,
  EndpointInfo,
  EndpointHealthStatus,
  EndpointMap,
} from "../types/serviceHealth.types";
import { ENDPOINT_KEYS } from "../types/serviceHealth.types";

const DEFAULT_TOP = 100;
const MAX_TOP = 1000;
const MIN_TOP = 1;
const CUSTOMER_ENVIRONMENTS_ENDPOINTS = [
  "service-health/customer-environments",
  "serviceHealth/customer-environments",
  "ServiceHealth/customer-environments",
];

function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

async function fetchCustomerEnvironmentsFromApi(sanitizedTop: number) {
  let lastError: unknown = null;

  for (const endpoint of CUSTOMER_ENVIRONMENTS_ENDPOINTS) {
    try {
      const response = await api.get(endpoint, {
        params: { top: sanitizedTop },
      });

      return { endpoint, data: response.data };
    } catch (error) {
      lastError = error;

      // Continue trying endpoint aliases only for 404 route misses.
      if (isNotFoundError(error)) {
        continue;
      }

      throw error;
    }
  }

  throw lastError ?? new Error("Failed to resolve service health endpoint");
}

function normalizeEndpointStatus(status: unknown): EndpointHealthStatus {
  if (status === "Healthy") return "Healthy";
  if (status === "Unhealthy") return "Unhealthy";
  if (status === "NotDeployed") return "NotDeployed";
  return "Unknown";
}

function mapDtoToCustomerEnvironment(dto: Partial<ApiCustomerEnvironment>): CustomerEnvironment {
  const endpoints = ENDPOINT_KEYS.reduce<EndpointMap<EndpointInfo>>((acc, key) => {
    const lastChecked = dto[`${key}_LastChecked`] ?? null;
    const status = normalizeEndpointStatus(dto[`${key}_Status`]);
    const statusCode = dto[`${key}_StatusCode`] ?? null;
    const url = dto[`${key}_URL`] ?? null;

    acc[key] = {
      lastChecked,
      status,
      statusCode,
      url,
    };

    return acc;
  }, {} as EndpointMap<EndpointInfo>);

  return {
    rowId: `${dto.PartitionKey ?? ""}:${dto.RowKey ?? ""}`,
    customerName: dto.CustomerName ?? "",
    environmentType: dto.EnvironmentType ?? "",
    product: dto.Product ?? "",
    useCase: dto.UseCase ?? "",
    endpoints,
  };
}

/**
 * Normalizes API payload shapes into a list of customer environments.
 * Supports direct arrays and wrapped payloads with items[].fields.
 * @param payload Raw API response payload.
 * @returns Normalized customer environments list.
 */
function normalizeCustomerEnvironmentsPayload(payload: unknown): CustomerEnvironment[] {
  if (Array.isArray(payload)) {
    if (payload.some((item) => !item || typeof item !== "object")) {
      throw new Error("Invalid service health payload: expected array items to be objects");
    }

    return payload
      .filter((item): item is Partial<ApiCustomerEnvironment> => !!item && typeof item === "object")
      .map(mapDtoToCustomerEnvironment);
  }

  if (payload && typeof payload === "object") {
    const wrapped = payload as { items?: unknown };

    if (Array.isArray(wrapped.items)) {
      return wrapped.items
        .map((item) => {
          if (!item || typeof item !== "object") {
            throw new Error("Invalid service health payload: expected wrapped item to be object");
          }

          const maybeWrapped = item as { fields?: unknown };
          if (!maybeWrapped.fields || typeof maybeWrapped.fields !== "object") {
            throw new Error(
              "Invalid service health payload: expected wrapped item to contain object fields",
            );
          }

          return maybeWrapped.fields as Partial<ApiCustomerEnvironment>;
        })
        .map(mapDtoToCustomerEnvironment);
    }
  }

  throw new Error("Invalid service health payload shape");
}

function sanitizeTop(top: number): number {
  if (!Number.isFinite(top)) {
    return DEFAULT_TOP;
  }

  const normalized = Math.floor(top);
  if (normalized < MIN_TOP) {
    return MIN_TOP;
  }

  if (normalized > MAX_TOP) {
    return MAX_TOP;
  }

  return normalized;
}

export const serviceHealthService = {
  getCustomerEnvironments: async (top = DEFAULT_TOP): Promise<CustomerEnvironment[]> => {
    const sanitizedTop = sanitizeTop(top);
    let resolvedEndpoint = CUSTOMER_ENVIRONMENTS_ENDPOINTS[0];

    try {
      const { endpoint, data } = await fetchCustomerEnvironmentsFromApi(sanitizedTop);
      resolvedEndpoint = endpoint;

      return normalizeCustomerEnvironmentsPayload(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";

      console.error("serviceHealthService.getCustomerEnvironments failed", {
        endpoint: resolvedEndpoint,
        attemptedEndpoints: CUSTOMER_ENVIRONMENTS_ENDPOINTS,
        requestedTop: top,
        sanitizedTop,
        expectedPayloadShape:
          "ApiCustomerEnvironment[] | { items: Array<{ fields: ApiCustomerEnvironment }> }",
        error: message,
      });

      throw new Error(`Failed to fetch customer environments: ${message}`);
    }
  },
};
